/** Form model for LogEmailRule.criteria — no JSON editor in the UI. */

export type LogTypeFilter = "any" | "foreground" | "background";

export type DataMatchRow = {
    path: string;
    /** Comma-separated values; one value → scalar, multiple → array. */
    valuesText: string;
};

export type AlertCriteriaForm = {
    topic: string;
    notesContains: string;
    logType: LogTypeFilter;
    /** When true, alert on any published class_id (OR-enabled classes). */
    matchEnabledClasses: boolean;
    dataRows: DataMatchRow[];
};

/** DetectionResult nested class path — placeholder hint only. */
export const DETECTION_CLASS_ID_PATH =
    "detections.detections.*.results.*.hypothesis.class_id";

export const emptyAlertCriteriaForm = (): AlertCriteriaForm => ({
    topic: "",
    notesContains: "",
    logType: "any",
    matchEnabledClasses: false,
    dataRows: [{ path: "", valuesText: "" }],
});

const parseValuesText = (text: string): string | string[] | null => {
    const parts = text
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    if (parts.length === 0) return null;
    if (parts.length === 1) return parts[0];
    return parts;
};

const valuesToText = (value: unknown): string => {
    if (value == null) return "";
    if (Array.isArray(value)) {
        return value.map((v) => String(v)).join(", ");
    }
    return String(value);
};

const isClassIdPath = (path: string): boolean =>
    path.endsWith(".class_id") || path.includes(".hypothesis.class_id");

export const criteriaToForm = (
    criteria: Record<string, unknown> | null | undefined,
): AlertCriteriaForm => {
    const c = criteria && typeof criteria === "object" ? criteria : {};
    const topic = typeof c.topic === "string" ? c.topic : "";
    const notesContains =
        typeof c.notes_contains === "string" ? c.notes_contains : "";
    const matchEnabledClasses = c.match_enabled_classes === true;

    let logType: LogTypeFilter = "any";
    if (typeof c.background === "boolean") {
        logType = c.background ? "background" : "foreground";
    }

    const data =
        c.data && typeof c.data === "object" && !Array.isArray(c.data)
            ? (c.data as Record<string, unknown>)
            : {};
    let dataRows: DataMatchRow[] = Object.entries(data).map(([path, value]) => ({
        path,
        valuesText: valuesToText(value),
    }));

    // When matching enabled OR classes, drop class_id rows from the editable list.
    if (matchEnabledClasses) {
        dataRows = dataRows.filter((row) => !isClassIdPath(row.path));
    }

    return {
        topic,
        notesContains,
        logType,
        matchEnabledClasses,
        dataRows: dataRows.length > 0 ? dataRows : [{ path: "", valuesText: "" }],
    };
};

export const formToCriteria = (form: AlertCriteriaForm): Record<string, unknown> => {
    const criteria: Record<string, unknown> = {};
    const topic = form.topic.trim();
    if (topic) {
        criteria.topic = topic;
    }
    const notes = form.notesContains.trim();
    if (notes) {
        criteria.notes_contains = notes;
    }
    if (form.logType === "foreground") {
        criteria.background = false;
    } else if (form.logType === "background") {
        criteria.background = true;
    }
    if (form.matchEnabledClasses) {
        criteria.match_enabled_classes = true;
    }

    const data: Record<string, unknown> = {};
    for (const row of form.dataRows) {
        const path = row.path.trim();
        if (!path) continue;
        // Do not persist class_id label lists when matching enabled classes.
        if (form.matchEnabledClasses && isClassIdPath(path)) continue;
        const parsed = parseValuesText(row.valuesText);
        if (parsed == null) continue;
        data[path] = parsed;
    }
    if (Object.keys(data).length > 0) {
        criteria.data = data;
    }
    return criteria;
};

/** One-line summary for rule list rows. */
export const summarizeCriteriaTopic = (
    criteria: Record<string, unknown> | null | undefined,
): string => {
    if (!criteria || typeof criteria !== "object") return "";
    return typeof criteria.topic === "string" ? criteria.topic : "";
};

/**
 * Class labels a rule cares about for UI filtering.
 * `null` means show all hits (match_enabled_classes or no class_id criteria).
 */
export const getRuleTargetClassIds = (
    criteria: Record<string, unknown> | null | undefined,
): string[] | null => {
    if (!criteria || typeof criteria !== "object") return null;
    if (criteria.match_enabled_classes === true) return null;

    const data =
        criteria.data && typeof criteria.data === "object" && !Array.isArray(criteria.data)
            ? (criteria.data as Record<string, unknown>)
            : {};

    const labels = new Set<string>();
    for (const [path, value] of Object.entries(data)) {
        if (!isClassIdPath(path)) continue;
        if (Array.isArray(value)) {
            for (const v of value) labels.add(String(v));
        } else if (value != null && value !== "") {
            labels.add(String(value));
        }
    }
    return labels.size > 0 ? Array.from(labels) : null;
};

/** Filter detection hits by rule criteria; pass-through when matching enabled classes. */
export const filterDetectionHitsByCriteria = <T extends { class_id: string }>(
    hits: T[],
    criteria: Record<string, unknown> | null | undefined,
): T[] => {
    const targets = getRuleTargetClassIds(criteria);
    if (!targets) return hits;
    const allowed = new Set(targets);
    return hits.filter((h) => allowed.has(h.class_id));
};
